import * as React from 'react'
import { RefreshCw } from 'lucide-react'
import { Cat, type CatMood } from './cats/Cat'
import { Button } from './ui/button'
import { SkeletonRows } from './ui/misc'
import { cn } from '@/lib/utils'
import { useT } from '@/i18n'
import type { Availability, ActionQueryResult } from '@/api/hooks'
import { errorMessage } from '@/api/hooks'

interface EmptyStateProps {
  mood?: CatMood
  title: React.ReactNode
  description?: React.ReactNode
  action?: React.ReactNode
  className?: string
  compact?: boolean
}

/** Empty state: cat illustration + one sentence + primary action. */
export function EmptyState({ mood = 'neutral', title, description, action, className, compact }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center text-center', compact ? 'gap-2 py-6' : 'gap-3 py-12', className)}>
      <Cat mood={mood} size={compact ? 56 : 88} />
      <div className="max-w-sm">
        <p className={cn('font-semibold text-ink-strong', compact ? 'text-[13px]' : 'text-[15px]')}>{title}</p>
        {description && <p className="mt-1 text-[13px] leading-relaxed text-muted">{description}</p>}
      </div>
      {action}
    </div>
  )
}

export function ModuleMissing({ compact, actionId }: { compact?: boolean; actionId?: string }) {
  const t = useT()
  if (compact) {
    // Calm, low-height variant for dashboard widgets and side panels.
    return (
      <div className="flex items-center gap-3 rounded-[10px] border border-dashed border-border-strong bg-surface-2 px-3 py-2.5" data-module-missing={actionId}>
        <Cat mood="sleepy" size={36} />
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-ink">{t('state.moduleMissing')}</p>
          {actionId && <code className="text-[11px] text-muted">{actionId}</code>}
        </div>
      </div>
    )
  }
  return (
    <EmptyState
      compact={compact}
      mood="sleepy"
      title={t('state.moduleMissing')}
      description={
        <>
          {t('state.moduleMissingHint')}
          {actionId && (
            <>
              {' '}
              <code className="rounded bg-sunken px-1 py-0.5 text-[11px]">{actionId}</code>
            </>
          )}
        </>
      }
    />
  )
}

export function NoAccess({ compact }: { compact?: boolean }) {
  const t = useT()
  return <EmptyState compact={compact} mood="angry" title={t('state.noAccess')} description={t('state.noAccessHint')} />
}

export function ErrorState({ error, onRetry, compact }: { error: unknown; onRetry?: () => void; compact?: boolean }) {
  const t = useT()
  return (
    <EmptyState
      compact={compact}
      mood="angry"
      title={t('state.error')}
      description={errorMessage(error)}
      action={
        onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw /> {t('common.retry')}
          </Button>
        )
      }
    />
  )
}

interface QueryStateProps<T> {
  query: ActionQueryResult<T>
  actionId?: string
  compact?: boolean
  skeleton?: React.ReactNode
  /** Shown when data is an empty array. */
  empty?: React.ReactNode
  children: (data: T) => React.ReactNode
}

/**
 * Renders the right state for an action query: skeleton, "module not connected",
 * "no access", error, empty, or the data.
 */
export function QueryState<T>({ query, actionId, compact, skeleton, empty, children }: QueryStateProps<T>) {
  const a: Availability = query.availability
  if (a === 'missing') return <ModuleMissing compact={compact} actionId={actionId} />
  if (a === 'forbidden') return <NoAccess compact={compact} />
  if (a === 'loading' || query.isPending) return <>{skeleton ?? <SkeletonRows rows={compact ? 3 : 6} />}</>
  if (query.isError) return <ErrorState compact={compact} error={query.error} onRetry={() => void query.refetch()} />
  const data = query.data as T
  if (empty && Array.isArray(data) && data.length === 0) return <>{empty}</>
  return <>{children(data)}</>
}
